/**
 * Print the quotation as the buyer sees it.
 *
 * Reads the staff endpoint rather than `/q/:token`, which is the whole reason
 * this route exists: a revoked or expired quotation answers 410 there, and it is
 * precisely after revocation that staff need the record of what was offered.
 * Fetching the token would also bump the buyer's view counter.
 *
 * A revoked quotation therefore still prints, and says so — the alternative is
 * having no paper record of an offer we actually made.
 */
import { formatMoney, selectionDocRef, sumLineTotals } from '@paradise/shared';
import { createFileRoute } from '@tanstack/react-router';

import {
  useProductItemNoLookup,
  useProductMeasureLookup,
  useProductThumbLookup
} from '@/components/lead-detail/use-product-covers';
import { DocumentTerms } from '@/components/print/document-terms';
import { documentSubtitle } from '@/components/print/document-date';
import { PrintDocumentPage } from '@/components/print/print-document-page';
import { PrintItemsTable } from '@/components/print/print-items-table';
import { useLeadQuoteQuery } from '@/components/print/use-lead-quote-query';
import { useLeadQuotations } from '@/components/quotation/use-lead-quotations';
import { mediaUrl } from '@/lib/media';

export const Route = createFileRoute('/_app/leads/$leadId/print/quotation/$quotationId')({
  staticData: { hideShellChrome: true },
  component: PrintQuotationRoute
});

function PrintQuotationRoute() {
  const { leadId, quotationId } = Route.useParams();
  const { data: leadResult } = useLeadQuoteQuery(leadId);
  // The same hook the quote and revise screens use, so this print shares their
  // React Query cache — revising a quotation and then printing it cannot show
  // two different documents.
  const { data: quotations, isPending } = useLeadQuotations(leadId);
  const photos = useProductThumbLookup();
  const measures = useProductMeasureLookup();
  const itemNos = useProductItemNoLookup();

  const lead = leadResult?.lead ?? null;
  const quotation = quotations?.find(entry => entry.id === quotationId) ?? null;
  const status = isPending ? 'Loading…' : quotation === null ? 'That quotation no longer exists.' : null;

  const currency = lead?.currency ?? 'INR';
  const total = quotation ? sumLineTotals(quotation.items) : null;
  // A revision must never be mistaken for the original it replaced, so the
  // ref carries it exactly as the lead screen spells it.
  const docRef = quotation
    ? `${selectionDocRef(quotation.id)}${quotation.revision > 1 ? ` R${String(quotation.revision)}` : ''}`
    : '';

  return (
    <PrintDocumentPage
      title="Quotation"
      subtitle={quotation ? documentSubtitle('Quotation', docRef, quotation.createdAt) : 'Quotation'}
      recipient={quotation ? { company: quotation.recipientCompany, contactName: quotation.recipientName } : null}
      leadId={leadId}
      footerRight={`Prices in ${currency}`}
      statusMessage={status}
    >
      {quotation && (
        <>
          {quotation.revokedAt !== null && (
            <p className="mb-3 rounded-md border border-destructive/40 px-2.5 py-1.5 text-[12.5px] text-destructive">
              This quotation was withdrawn. Kept as the record of an offer that was made.
            </p>
          )}
          {/* No `showListPrice`: the staff `Quotation` rows carry
              `listPriceSnapshot`, but that is our cost basis and this sheet goes
              to a buyer. The table defaults to hiding it, so this is safe by
              omission rather than by remembering to strip the field. */}
          <PrintItemsTable
            items={quotation.items}
            currency={currency}
            photos={photos}
            photoUrl={key => mediaUrl('products', key)}
            measures={measures}
            itemNos={itemNos}
          />
          <div className="mt-2.5 flex items-baseline justify-between border-t pt-2">
            <span className="text-[12.5px] text-muted-foreground">Total</span>
            <span className="font-heading text-base font-semibold">{formatMoney(total, currency)}</span>
          </div>
          <DocumentTerms terms={quotation.terms} />
        </>
      )}
    </PrintDocumentPage>
  );
}
